/**
 * JWT Session Management Hook
 *
 * OPERATIONAL FLOW:
 * ================
 *
 * 1. DUAL TIMER SYSTEM:
 *    - Activity Timer: Tracks user inactivity (10 min timeout)
 *    - Token Timer: Manages JWT lifecycle (refresh at 9 min, expire at 10 min)
 *
 * 2. USER ACTIVITY TRACKING:
 *    - Monitors mouse, keyboard, touch events
 *    - Throttles updates to every 5 seconds (performance)
 *    - Resets activity timer on each interaction
 *    - Stores last activity timestamp for token refresh decisions
 *
 * 3. TOKEN REFRESH LOGIC (before token expiry):
 *    - Always refreshes when timer fires (no activity gate)
 *    - Activity timer separately handles inactivity logout
 *    - On success: Updates token, resets token timer using backend expiresIn
 *    - On failure: Logs out user immediately
 *    - On cooldown: Retries after cooldown expires
 *
 * 4. LOGOUT CONDITIONS:
 *    - 10 minutes of user inactivity (activity timer)
 *    - Token refresh failure
 *    - No refresh function & token expired (10 min)
 *
 * 5. SPECIAL CASES:
 *    - Login page WITHOUT session-critical modal: Skip logout (user not authenticated)
 *    - Login page WITH session-critical modal: Force logout (security requirement)
 *    - Uses sessionModalTracker for checking active modals (no Redux dependency)
 *
 * 6. SESSION PERSISTENCE:
 *    - Survives component unmounts/navigation
 *    - Stores timer state in sessionStorage
 *    - Restores timers with remaining time on remount
 */

import { useEffect, useCallback, useState, useRef } from "react";
import { useSelector, useStore } from "react-redux";
import { Persistor } from "redux-persist";
import { QueryClient } from "react-query";
import { sessionModalTracker } from "../Utils/sessionModalTracker";
import { performStateCleanup } from "../services/standardizedLogout";

import { SessionInvalidatedError } from "../Utils/axiosSessionInterceptor";
import {
  isSessionInvalidationInProgress,
  claimExpiredRedirect,
} from "../Utils/sessionState";
import { RefreshCooldownError } from "../hooks/useRefreshAuth";

const ACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes
const TOKEN_TIMEOUT = 10 * 60 * 1000; // 10 minutes
const DEFAULT_TOKEN_REFRESH_THRESHOLD = 9 * 60 * 1000; // Fallback: refresh at 9 minutes
const REFRESH_BUFFER_MS = 60 * 1000; // Refresh 1 minute before expiry
const ACTIVITY_THROTTLE_MS = 5000; // 5 seconds - limit timer resets
const ACTIVITY_EVENTS = ["mousedown", "keypress", "touchstart", "click"];

/**
 * Parse duration string (e.g. "15m", "1h", "900s") to milliseconds.
 */
const parseDurationToMs = (duration: string): number | null => {
  const match = duration.match(/^(\d+)(s|m|h)$/);
  if (!match) return null;
  const value = parseInt(match[1], 10);
  switch (match[2]) {
    case "s": return value * 1000;
    case "m": return value * 60 * 1000;
    case "h": return value * 60 * 60 * 1000;
    default: return null;
  }
};

type TokenSelector<T = any> = (state: T) => { token: string };
type TokenRefreshFunction = () => Promise<string | undefined>;

export const useJwtExpiry = <T = any>(
  tokenSelector: TokenSelector<T>,
  refreshToken?: TokenRefreshFunction,
  persistor?: Persistor,
  queryClient?: QueryClient,
  resetActions?: Array<() => { type: string }>
) => {
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Refs to track current state values in timer callbacks (avoids stale closures)
  const isRefreshingRef = useRef(false);
  const isLoggingOutRef = useRef(false);
  isRefreshingRef.current = isRefreshing;
  isLoggingOutRef.current = isLoggingOut;

  // Track last active time
  const lastActiveTime = useRef<number>(Date.now());
  const lastActivityUpdate = useRef<number>(0);

  // Timers
  const activityTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tokenTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Dynamic refresh threshold based on backend expiresIn (refresh 1 min before expiry)
  const refreshThresholdRef = useRef<number>(DEFAULT_TOKEN_REFRESH_THRESHOLD);

  const store = useStore();
  const { token } = useSelector(tokenSelector);

  const handleLogout = useCallback(async () => {
    // Prevent multiple simultaneous logout attempts
    if (isLoggingOutRef.current) {
      return;
    }

    // Check if on login page
    const currentPath = window.location.pathname;
    const isOnLoginPage = currentPath.includes("/login");

    // Check if any session-critical modal is active
    const hasActiveSessionModal = sessionModalTracker.hasActiveModals();

    // Skip logout if on login page and NO session-critical modal is open
    // This allows users to remain on login page without being redirected
    // but ensures logout when a session-critical modal is open (security requirement)
    if (isOnLoginPage && !hasActiveSessionModal) {
      return;
    }

    // Set ref immediately to prevent double-execution before next render
    isLoggingOutRef.current = true;
    setIsLoggingOut(true);

    try {
      // Clear all timers
      if (activityTimer.current) clearTimeout(activityTimer.current);
      if (tokenTimer.current) clearTimeout(tokenTimer.current);

      // Wipe state immediately to prevent any further API calls (including refresh)
      await performStateCleanup({ store, resetActions, persistor, queryClient });

      // Set flag AFTER cleanup (sessionStorage.clear() already ran) so login page can show overlay
      sessionStorage.setItem("sessionExpired", "true");
    } catch (error) {
      console.error("Logout cleanup failed:", error);
    }

    // State cleanup resets the token, which triggers PrivateRoute to navigate
    // to /login via <Navigate>. If that soft navigation hasn't moved us to /login
    // (e.g., user was on a public route), force a hard redirect as fallback.
    // The mutex ensures only one handler across the app performs this redirect.
    if (!window.location.pathname.includes("/login")) {
      if (claimExpiredRedirect()) {
        window.location.replace("/login");
      }
    }
  }, [store, persistor, queryClient, resetActions]);

  const resetActivityTimer = useCallback(() => {
    // Reset the activity timer - user will be logged out after 10 minutes of inactivity
    if (activityTimer.current) {
      clearTimeout(activityTimer.current);
    }

    activityTimer.current = setTimeout(() => {
      // Activity timeout reached - log out the user
      handleLogout();
    }, ACTIVITY_TIMEOUT);
  }, [handleLogout]);

  const resetTokenTimer = useCallback(
    (newToken?: string) => {
      if (tokenTimer.current) {
        clearTimeout(tokenTimer.current);
      }

      // Store the start time for this timer
      const startTime = Date.now();
      sessionStorage.setItem("hash", startTime.toString());

      // Store the current token in AuthPrev (before it gets refreshed)
      // Only update AuthPrev if we have a token and it's different from what's stored
      const currentAuthPrev = sessionStorage.getItem("AuthPrev");
      const currentToken = newToken || token;

      if (currentToken && currentToken !== "none") {
        // If this is a new token (after refresh), the old token should already be in AuthPrev
        // If this is initial setup, store the current token
        if (
          !currentAuthPrev ||
          (newToken && currentAuthPrev !== currentToken)
        ) {
          sessionStorage.setItem("AuthPrev", currentToken);
        }
      }

      const refreshTime = refreshToken
        ? refreshThresholdRef.current
        : TOKEN_TIMEOUT;

      // Set timer for token refresh/expiry
      tokenTimer.current = setTimeout(() => {
        if (refreshToken && !isRefreshingRef.current && !isLoggingOutRef.current) {
          // Always refresh when timer fires — letting the token expire is worse
          // than one "unnecessary" refresh. The activity timer handles inactivity logout.
          setIsRefreshing(true);

          refreshToken()
            .then((expiresIn) => {
              // Token refresh successful — update threshold from backend expiresIn
              if (expiresIn) {
                const expiryMs = parseDurationToMs(expiresIn);
                if (expiryMs && expiryMs > REFRESH_BUFFER_MS) {
                  refreshThresholdRef.current = expiryMs - REFRESH_BUFFER_MS;
                }
              }
              // Note: don't call resetTokenTimer here - the effect will handle it
              // when it detects the new token
            })
            .catch((error) => {
              // If session was invalidated, the interceptor already handled cleanup — don't double-logout
              if (
                error instanceof SessionInvalidatedError ||
                isSessionInvalidationInProgress()
              )
                return;
              // Cooldown hit — retry after it expires
              if (error instanceof RefreshCooldownError) {
                tokenTimer.current = setTimeout(() => {
                  resetTokenTimer();
                }, error.remainingMs);
                return;
              }
              // Token refresh failed for other reasons - log out
              handleLogout();
            })
            .finally(() => {
              setIsRefreshing(false);
            });
        } else if (!refreshToken) {
          // If no refresh function provided, logout at token expiry
          handleLogout();
        }
      }, refreshTime);
    },
    [refreshToken, handleLogout, token]
  );

  const handleUserActivity = useCallback(() => {
    const now = Date.now();

    // Always update the activity time for accurate inactivity detection
    lastActiveTime.current = now;

    // But only reset timer if throttle period has passed to prevent excessive calls
    if (now - lastActivityUpdate.current >= ACTIVITY_THROTTLE_MS) {
      lastActivityUpdate.current = now;
      // User activity detected - reset activity timer (throttled)
      resetActivityTimer();
    }
  }, [resetActivityTimer]);

  // Initialize activity timer and listeners only once when hook mounts
  useEffect(() => {
    // Initialize timestamps
    lastActiveTime.current = Date.now();
    lastActivityUpdate.current = Date.now();

    // Set up activity timer (only once)
    resetActivityTimer();

    // Set up activity listeners
    ACTIVITY_EVENTS.forEach((eventType) => {
      document.addEventListener(eventType, handleUserActivity, {
        passive: true,
      });
    });

    // Cleanup function
    return () => {
      // Clear activity timer
      if (activityTimer.current) {
        clearTimeout(activityTimer.current);
      }

      // Remove activity listeners
      ACTIVITY_EVENTS.forEach((eventType) => {
        document.removeEventListener(eventType, handleUserActivity);
      });
    };
  }, []); // Empty dependency array - runs only once

  // Helper to trigger token refresh
  const triggerTokenRefresh = useCallback(() => {
    if (!refreshToken || isRefreshingRef.current || isLoggingOutRef.current) {
      if (!refreshToken) handleLogout();
      return;
    }

    setIsRefreshing(true);
    refreshToken()
      .then((expiresIn) => {
        if (expiresIn) {
          const expiryMs = parseDurationToMs(expiresIn);
          if (expiryMs && expiryMs > REFRESH_BUFFER_MS) {
            refreshThresholdRef.current = expiryMs - REFRESH_BUFFER_MS;
          }
        }
      })
      .catch((error) => {
        // If session was invalidated, the interceptor already handled cleanup — don't double-logout
        if (
          error instanceof SessionInvalidatedError ||
          isSessionInvalidationInProgress()
        )
          return;
        // Cooldown hit — retry after it expires
        if (error instanceof RefreshCooldownError) {
          tokenTimer.current = setTimeout(() => {
            resetTokenTimer();
          }, error.remainingMs);
          return;
        }
        console.error("Token refresh failed:", error);
        handleLogout();
      })
      .finally(() => setIsRefreshing(false));
  }, [refreshToken, handleLogout]);

  // Helper to restore timer with remaining time
  const restoreTimerWithRemainingTime = useCallback(
    (startTime: number) => {
      const elapsed = Date.now() - startTime;
      const timeout = refreshToken ? refreshThresholdRef.current : TOKEN_TIMEOUT;
      const remaining = timeout - elapsed;

      if (remaining > 0) {
        if (tokenTimer.current) clearTimeout(tokenTimer.current);
        tokenTimer.current = setTimeout(triggerTokenRefresh, remaining);
      } else {
        triggerTokenRefresh();
      }
    },
    [refreshToken, triggerTokenRefresh]
  );

  // Handle token timer - only initialize once or when token actually changes
  // Use sessionStorage to persist state across component remounts during navigation
  useEffect(() => {
    if (!token || token === "none") {
      // No valid token - clear timer and storage
      if (tokenTimer.current) clearTimeout(tokenTimer.current);
      sessionStorage.removeItem("AuthPrev");
      sessionStorage.removeItem("hash");
      return;
    }

    if (isRefreshing) {
      // Token is being refreshed - skip timer setup
      return;
    }

    const AuthPrev = sessionStorage.getItem("AuthPrev");
    const startTimeStr = sessionStorage.getItem("hash");
    const isNewToken = AuthPrev !== token;
    const hasExistingTimer = !!startTimeStr;

    // Case 1: First initialization or token changed (after refresh)
    if (!hasExistingTimer || isNewToken) {
      resetTokenTimer(token);
      return;
    }

    // Case 2: Hook remounted (navigation) - restore timer with remaining time
    restoreTimerWithRemainingTime(parseInt(startTimeStr, 10));
  }, [token, resetTokenTimer, isRefreshing, restoreTimerWithRemainingTime]);

  return {
    isRefreshing,
    isLoggingOut,
    lastActiveTime: lastActiveTime.current,
  };
};
